/** .tuMetrics {"tu1": 0.88, "tu2": 0.90, "tu3": 5, "notes": "Frontend client for teambots-integration; includes assign wake", "tuVersion": "2.5"} * .help "Invoke TeamBots org status, connect org, lookup/connect agents, and card-assign wake." */
import { supabase } from '../../lib/supabase';

export type TeamBotsOrgStatus = {
  enabled: boolean;
  teambotsOrgId: string | null;
  connectionStatus: string | null;
  connectedAt: string | null;
};

export type TeamBotsAgentLookup = {
  agentId: string;
  name: string;
  emailIdentity: string;
  status: string;
  organizationId: string;
  organizationSlug: string;
  organizationName: string;
  humanSponsor: string;
  avatar: { initials: string; tint: string; imageUrl?: string };
  jobTitle?: string;
  jobFunction?: string;
  department?: string;
  profileName?: string;
  starterProfileSlug?: string;
  jobDescription?: string;
  canConnect: boolean;
};

type InvokeBody = Record<string, unknown>;

async function invokeTeamBots<T>(body: InvokeBody): Promise<T> {
  const { data, error } = await supabase.functions.invoke('teambots-integration', { body });

  if (error) {
    let message = error.message;
    const ctx = (error as { context?: { error?: string } }).context;
    if (ctx?.error) {
      try {
        const parsed = JSON.parse(ctx.error);
        message = parsed.error || ctx.error;
      } catch {
        message = ctx.error;
      }
    }
    throw new Error(message);
  }

  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error));
  }

  return data as T;
}

export async function getTeamBotsOrgStatus(companyId: string): Promise<TeamBotsOrgStatus> {
  return invokeTeamBots({ action: 'get_org_status', companyId });
}

export async function connectTeamBotsOrg(
  companyId: string,
  teambotsOrgId: string,
  verifiedDomains?: string[],
): Promise<void> {
  await invokeTeamBots({
    action: 'connect_org',
    companyId,
    teambotsOrgId,
    verifiedDomains,
  });
}

export async function lookupTeamBotsAgent(
  companyId: string,
  email: string,
): Promise<{ agent: TeamBotsAgentLookup; blockedReason: string | null; canConnect: boolean }> {
  return invokeTeamBots({ action: 'lookup_agent', companyId, email });
}

export async function connectTeamBotsAgent(
  companyId: string,
  email: string,
  teamId: string | null,
): Promise<{ userId: string }> {
  return invokeTeamBots({ action: 'connect_agent', companyId, email, teamId });
}

export async function refreshTeamBotsAgent(
  companyId: string,
  userId: string,
): Promise<void> {
  await invokeTeamBots({ action: 'refresh_agent', companyId, userId });
}

export type TeamBotsDesktopSession = {
  desktopSessionUrl: string | null;
  partnerAgentRoutingMode: string | null;
};

/**
 * Fetch a fresh OpenClaw/Kasm desktop URL for a federated agent. Kasm sessions
 * rotate independently of reconnect, so we resolve on demand (button click) and
 * the edge function falls back to the stored URL if TeamBots returns nothing.
 */
export async function getTeamBotsDesktopSessionUrl(
  companyId: string,
  userId: string,
): Promise<TeamBotsDesktopSession> {
  return invokeTeamBots<TeamBotsDesktopSession>({
    action: 'get_desktop_session_url',
    companyId,
    userId,
  });
}

export type TeamBotsWakeEventType =
  | 'task_assigned'
  | 'mention'
  | 'review_requested'
  | 'due_date';

export type TeamBotsWakePlan = {
  title?: string;
  weight?: string;
  components?: string[];
  gitSteps?: string[];
  branch?: string;
  approvalRequestId?: string;
};

export type TeamBotsWakeStart = {
  wakeId: string;
  status: 'running' | 'needs_approval';
  message?: string;
  cardStatus?: string;
  replyTarget?: 'card' | 'dm' | 'none';
  plan?: TeamBotsWakePlan | null;
  reusedExisting?: boolean;
};

export type TeamBotsWakeStatus = {
  wakeId: string;
  status: 'running' | 'completed' | 'failed' | 'needs_approval';
  message?: string;
  content?: string;
  error?: string;
  plan?: TeamBotsWakePlan;
};

/** Start OpenClaw wake (card assign or generic document/mention event). */
export async function startTeamBotsWake(input: {
  companyId: string;
  localAgentId: string;
  actorName?: string;
  cardId?: string;
  cardUrl?: string;
  eventType?: TeamBotsWakeEventType;
  summary?: string;
  body?: string;
  entity?: { taskId: string; taskTitle: string; url?: string };
  replyTarget?: 'card' | 'dm' | 'none';
}): Promise<TeamBotsWakeStart> {
  return invokeTeamBots<TeamBotsWakeStart>({
    action: 'currents_wake',
    companyId: input.companyId,
    localAgentId: input.localAgentId,
    actorName: input.actorName,
    cardId: input.cardId,
    cardUrl: input.cardUrl,
    eventType: input.eventType,
    summary: input.summary,
    body: input.body,
    entity: input.entity,
    replyTarget: input.replyTarget,
  });
}

/** Poll wake until completed/failed; edge writes the reply to the card or DM once. */
export async function getTeamBotsWakeStatus(input: {
  companyId: string;
  wakeId: string;
  localAgentId: string;
  cardId?: string;
  senderId?: string;
  replyTarget?: 'card' | 'dm' | 'none';
}): Promise<TeamBotsWakeStatus> {
  return invokeTeamBots<TeamBotsWakeStatus>({
    action: 'currents_wake_status',
    companyId: input.companyId,
    wakeId: input.wakeId,
    cardId: input.cardId,
    localAgentId: input.localAgentId,
    senderId: input.senderId,
    replyTarget: input.replyTarget,
  });
}

export function teambotsAvatarUrl(agent: TeamBotsAgentLookup): string {
  const imageUrl = agent.avatar?.imageUrl?.trim();
  if (imageUrl && /^https?:\/\//i.test(imageUrl)) return imageUrl;

  const initials = agent.avatar?.initials || agent.name?.charAt(0) || 'A';
  const tint = (agent.avatar?.tint || '#6366f1').replace('#', '');
  return `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(initials)}&backgroundColor=${tint}`;
}
